import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

// Configure axios with credentials for authentication
const axiosWithAuth = axios.create({
  withCredentials: true,
});
import {
  X,
  CheckCircle2,
  Clock,
  Target,
  MapPin,
  Phone,
  User,
  Calendar,
  Clipboard,
  FileText,
  Send,
  Link2,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Edit3,
  Plus,
  Trash2,
  ArrowLeft,
  Download,
  Check,
  DollarSign,
  ListChecks,
  Star,
  Shield
} from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ClientSOWBuilder = ({
  estimateId,
  estimateData,
  clientInfo,
  companySettings,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [publicToken, setPublicToken] = useState(null);
  
  // Editable header fields
  const [documentId, setDocumentId] = useState("");
  const [validDays, setValidDays] = useState("30");
  
  // Project overview
  const [projectOverview, setProjectOverview] = useState("");
  
  // Scope sections from estimate
  const [scopeSections, setScopeSections] = useState([]);
  
  // Payment milestones
  const [paymentMilestones, setPaymentMilestones] = useState([
    { id: 1, name: "Project Commencement", description: "Upon signing and material ordering", percentage: 25 },
    { id: 2, name: "Framing Complete", description: "Structural framing inspection passed", percentage: 25 },
    { id: 3, name: "Substantial Completion", description: "All major work completed", percentage: 40 },
    { id: 4, name: "Final Completion", description: "Punch list complete, final walkthrough", percentage: 10 }
  ]);
  
  // Project timeline
  const [projectTimeline, setProjectTimeline] = useState([
    { id: 1, phase: "Mobilization & Materials", duration: "1-2 days" },
    { id: 2, phase: "Foundation/Footings", duration: "2-3 days" },
    { id: 3, phase: "Framing & Structure", duration: "3-5 days" },
    { id: 4, phase: "Finishing & Details", duration: "2-3 days" },
    { id: 5, phase: "Final Inspection & Cleanup", duration: "1 day" }
  ]);
  
  // Terms
  const [terms, setTerms] = useState([
    "All work performed in accordance with local building codes and regulations",
    "Permits and inspections are the responsibility of the contractor unless otherwise noted",
    "Any changes to the scope of work must be documented in a written change order",
    "Payment terms as outlined in the milestone schedule above",
    "Warranty: 1 year workmanship, manufacturer warranties on materials"
  ]);
  
  // Exclusions
  const [exclusions, setExclusions] = useState([
    "Unforeseen structural repairs or modifications",
    "Landscaping restoration beyond immediate work area",
    "Interior work not specified in scope",
    "Utility upgrades or modifications"
  ]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Get groups from estimate data
  const groupsFromProps = estimateData?.groups || estimateData?.allScopes?.base?.groups || [];
  
  // State for groups fetched from backend (fallback when props are stale)
  const [fetchedGroups, setFetchedGroups] = useState([]);
  
  // Use fetched groups if props are empty but we have an estimateId
  const groups = groupsFromProps.length > 0 ? groupsFromProps : fetchedGroups;
  
  // API base URL
  const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
  
  // Fetch estimate data from backend when props are empty (fixes stale state issue)
  useEffect(() => {
    const fetchEstimateData = async () => {
      // Only fetch if we have an estimateId but props are empty
      if (estimateId && groupsFromProps.length === 0) {
        console.log("[ClientSOWBuilder] Props are empty, fetching from backend...");
        try {
          const response = await axiosWithAuth.get(`${API_BASE}/estimates/${estimateId}/frontend-scopes`);
          const scopes = response.data?.scopes;
          if (scopes?.base?.groups && scopes.base.groups.length > 0) {
            console.log("[ClientSOWBuilder] Fetched groups from backend:", scopes.base.groups.length);
            setFetchedGroups(scopes.base.groups);
          }
        } catch (error) {
          console.warn("[ClientSOWBuilder] Could not fetch estimate data:", error);
        }
      }
    };
    
    fetchEstimateData();
  }, [estimateId, groupsFromProps.length, API_BASE]);
  
  // Calculate grand total
  const calculateTotal = useCallback(() => {
    let total = 0;
    groups.forEach(group => {
      (group.items || []).forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        const unitCost = parseFloat(item.unitCost) || 0;
        const markup = parseFloat(item.markupPercent) || 0;
        const itemTotal = qty * unitCost * (1 + markup / 100);
        total += itemTotal;
      });
    });
    return total;
  }, [groups]);

  const grandTotal = calculateTotal();
  
  // Load or create client SOW
  useEffect(() => {
    const loadOrCreateSOW = async () => {
      // CRITICAL FIX: Allow rendering with live data even without estimateId
      const hasLiveData = groups && groups.length > 0;
      
      if (!estimateId && !hasLiveData) {
        setLoading(false);
        return;
      }
      
      if (!estimateId && hasLiveData) {
        // No ID but we have live data - use it directly
        console.log("[ClientSOWBuilder] Using live data (no estimateId)");
        setLoading(false);
        return;
      }
      
      try {
        // Try to load existing SOW
        const response = await axiosWithAuth.get(`${API_BASE}/client-sow/${estimateId}`);
        
        if (response.data && response.data.public_token) {
          setPublicToken(response.data.public_token);
          // Load saved data if present
          if (response.data.project_overview) setProjectOverview(response.data.project_overview);
          if (response.data.scope_sections?.length > 0) setScopeSections(response.data.scope_sections);
          if (response.data.payment_milestones?.length > 0) setPaymentMilestones(response.data.payment_milestones);
          if (response.data.project_timeline?.length > 0) setProjectTimeline(response.data.project_timeline);
          if (response.data.terms?.length > 0) setTerms(response.data.terms);
          if (response.data.exclusions?.length > 0) setExclusions(response.data.exclusions);
          if (response.data.valid_days) setValidDays(String(response.data.valid_days));
          setLoading(false);
          return;
        }
      } catch (error) {
        // SOW doesn't exist - that's OK, we'll use live data
        console.log("[ClientSOWBuilder] No saved SOW, using live data");
      }
      
      // Only try to create if we have an estimateId
      if (estimateId) {
        try {
          // Create new SOW
          const createResponse = await axiosWithAuth.post(`${API_BASE}/client-sow`, {
            estimate_id: estimateId,
            project_name: clientInfo?.project_name || estimateData?.job_name || "Project",
            project_address: clientInfo?.address || "",
            client_name: clientInfo?.name || "",
            client_email: clientInfo?.email || "",
            client_phone: clientInfo?.phone || "",
            company_name: companySettings?.company_name || "",
            total_value: grandTotal
          });
          
          if (createResponse.data?.public_token) {
            setPublicToken(createResponse.data.public_token);
          }
        } catch (error) {
          console.warn("[ClientSOWBuilder] Failed to create SOW, using live data:", error);
        }
      }
      
      setLoading(false);
    };
    
    loadOrCreateSOW();
  }, [estimateId, API_BASE, clientInfo, companySettings, estimateData, grandTotal, groups]);

  // ═══════════════════════════════════════════════════════════════
  // FETCH CHATGPT TIMELINE DATA
  // Only updates timeline durations - keeps all other content static
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const fetchChatGPTTimeline = async () => {
      if (!estimateId) return;
      
      try {
        const response = await axiosWithAuth.get(`${API_BASE}/estimates/${estimateId}`);
        const missionBrief = response.data?.mission_brief_documents;
        
        if (missionBrief) {
          // Update timeline durations from ChatGPT operator_order phases
          const operatorOrder = missionBrief.operator_order;
          
          if (operatorOrder?.phases && operatorOrder.phases.length > 0) {
            // Map ChatGPT phases to timeline with accurate durations
            const chatgptTimeline = operatorOrder.phases.map((phase, idx) => ({
              id: idx + 1,
              phase: phase.phase_name || `Phase ${idx + 1}`,
              duration: phase.week_range || `${phase.duration_days?.typical || phase.duration_days?.high || 7} days`
            }));
            
            setProjectTimeline(chatgptTimeline);
            console.log("[ClientSOWBuilder] Updated timeline from ChatGPT:", chatgptTimeline);
          }
          
          // Update payment milestones from ChatGPT milestone_objectives
          const milestoneData = missionBrief.milestone_objectives;
          if (milestoneData?.draws && milestoneData.draws.length > 0) {
            const chatgptMilestones = milestoneData.draws.map((draw, idx) => ({
              id: idx + 1,
              name: draw.milestone_name || draw.milestone || `Draw ${idx + 1}`,
              description: draw.completion_criteria?.[0] || "Per contract terms",
              percentage: draw.percentage || 25
            }));
            
            setPaymentMilestones(chatgptMilestones);
            console.log("[ClientSOWBuilder] Updated milestones from ChatGPT:", chatgptMilestones);
          }
        }
      } catch (error) {
        // No ChatGPT data available - keep defaults (this is fine)
        console.log("[ClientSOWBuilder] No ChatGPT data, using defaults");
      }
    };
    
    fetchChatGPTTimeline();
  }, [estimateId, API_BASE]);

  // Save SOW data
  const saveSOW = useCallback(async () => {
    if (!estimateId) {
      toast.error("Save estimate first to enable saving documents");
      return;
    }
    
    setSaving(true);
    try {
      await axiosWithAuth.post(`${API_BASE}/client-sow`, {
        estimate_id: estimateId,
        document_id: documentId,
        valid_days: parseInt(validDays) || 30,
        project_overview: projectOverview,
        scope_sections: scopeSections,
        payment_milestones: paymentMilestones,
        project_timeline: projectTimeline,
        terms,
        exclusions,
        total_value: grandTotal,
        project_name: clientInfo?.project_name || estimateData?.job_name,
        project_address: clientInfo?.address || "",
        client_name: clientInfo?.name || "",
        client_email: clientInfo?.email || "",
        client_phone: clientInfo?.phone || "",
        company_name: companySettings?.company_name || ""
      });
      toast.success("Scope of Work saved");
    } catch (error) {
      console.error("Failed to save SOW:", error);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  }, [estimateId, API_BASE, documentId, validDays, projectOverview, scopeSections, paymentMilestones, projectTimeline, terms, exclusions, grandTotal, clientInfo, companySettings, estimateData]);

  // Initialize scope sections from estimate
  useEffect(() => {
    if (groups.length > 0 && scopeSections.length === 0) {
      const sections = groups.map((group, idx) => ({
        id: idx,
        name: group.name,
        description: generateScopeDescription(group.name),
        items: (group.items || []).map((item, itemIdx) => ({
          id: itemIdx,
          name: item.itemName || item.name || "Item",
          description: item.description || "",
          included: true
        })),
        total: (group.items || []).reduce((sum, item) => {
          const qty = parseFloat(item.quantity) || 0;
          const unitCost = parseFloat(item.unitCost) || 0;
          const markup = parseFloat(item.markupPercent) || 0;
          return sum + (qty * unitCost * (1 + markup / 100));
        }, 0)
      }));
      setScopeSections(sections);
    }
    
    setDocumentId(`SOW-${(estimateId || "00001").toUpperCase().slice(-8)}`);
    
    // Generate project overview
    const jobName = clientInfo?.project_name || estimateData?.job_name || "your project";
    setProjectOverview(`This Scope of Work document outlines the complete specifications for ${jobName}. All work will be performed by licensed professionals using quality materials and adhering to local building codes. Our team is committed to delivering exceptional craftsmanship and clear communication throughout the project.`);
  }, [groups, estimateId, clientInfo, estimateData]);

  // Generate scope description based on group name
  const generateScopeDescription = (groupName) => {
    const name = (groupName || "").toLowerCase();
    if (name.includes("deck") || name.includes("framing")) {
      return "Complete deck construction including all structural framing, decking installation, and finishing details per approved plans.";
    }
    if (name.includes("railing") || name.includes("rail")) {
      return "Professional railing system installation including posts, balusters, top rails, and all required hardware meeting code requirements.";
    }
    if (name.includes("stair") || name.includes("step")) {
      return "Custom stair construction with proper rise/run calculations, stringers, treads, risers, and handrails per code.";
    }
    if (name.includes("footer") || name.includes("post") || name.includes("foundation")) {
      return "Foundation work including excavation, concrete footings, post installation, and proper drainage considerations.";
    }
    return `Complete ${groupName || "work"} as specified, including all labor, materials, and finishing to industry standards.`;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: prev[sectionId] === false ? true : false
    }));
  };

  // Edit handlers for milestones
  const handleEditMilestone = (idx, field, value) => {
    setPaymentMilestones(prev => prev.map((m, i) => 
      i === idx ? { ...m, [field]: field === 'percentage' ? parseFloat(value) || 0 : value } : m
    ));
  };

  const handleAddMilestone = () => {
    setPaymentMilestones(prev => [...prev, {
      id: Date.now(),
      name: "New Milestone",
      description: "Description",
      percentage: 0
    }]);
  };

  const handleDeleteMilestone = (idx) => {
    setPaymentMilestones(prev => prev.filter((_, i) => i !== idx));
  };

  // Edit handlers for timeline
  const handleEditTimeline = (idx, field, value) => {
    setProjectTimeline(prev => prev.map((t, i) => 
      i === idx ? { ...t, [field]: value } : t
    ));
  };

  const handleAddTimeline = () => {
    setProjectTimeline(prev => [...prev, {
      id: Date.now(),
      phase: "New Phase",
      duration: "TBD"
    }]);
  };

  const handleDeleteTimeline = (idx) => {
    setProjectTimeline(prev => prev.filter((_, i) => i !== idx));
  };

  // Edit handlers for terms
  const handleEditTerm = (idx, value) => {
    setTerms(prev => prev.map((t, i) => i === idx ? value : t));
  };

  const handleAddTerm = () => {
    setTerms(prev => [...prev, "New term"]);
  };

  const handleDeleteTerm = (idx) => {
    setTerms(prev => prev.filter((_, i) => i !== idx));
  };

  // Edit handlers for exclusions
  const handleEditExclusion = (idx, value) => {
    setExclusions(prev => prev.map((e, i) => i === idx ? value : e));
  };

  const handleAddExclusion = () => {
    setExclusions(prev => [...prev, "New exclusion"]);
  };

  const handleDeleteExclusion = (idx) => {
    setExclusions(prev => prev.filter((_, i) => i !== idx));
  };

  // Edit scope section description
  const handleEditScopeDescription = (sectionIdx, value) => {
    setScopeSections(prev => prev.map((s, i) => 
      i === sectionIdx ? { ...s, description: value } : s
    ));
  };

  // Calculate milestone total percentage
  const totalPercentage = paymentMilestones.reduce((sum, m) => sum + m.percentage, 0);

  // Check if we have data to work with (either estimateId OR groups with data)
  const hasEstimateData = estimateId || (groups && groups.length > 0);
  
  // EMPTY STATE: No estimate data available
  if (!hasEstimateData) {
    return (
      <div className="min-h-screen pt-14" style={{ backgroundColor: "#020617" }}>
        {/* Header Bar - Fixed at top */}
        <div 
          className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between"
          style={{ 
            backgroundColor: "#0f172a",
            borderBottom: "2px solid rgba(16, 185, 129, 0.5)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)"
          }}
        >
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-all font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>← Exit</span>
          </button>
          
          <div className="flex items-center gap-3">
            <span className="text-emerald-400 font-bold">CLIENT SCOPE OF WORK</span>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center justify-center" style={{ minHeight: "calc(100vh - 60px)" }}>
          <div className="text-center p-8 rounded-lg" style={{ backgroundColor: "#0f172a", border: "2px dashed #334155", maxWidth: "500px" }}>
            <h2 className="text-xl font-bold text-white mb-4">No Estimate Data</h2>
            <p className="text-slate-400 mb-6">Create an estimate first using the ESTIMATING COMMAND, then return here to build your Client SOW.</p>
            <button
              onClick={onClose}
              className="px-6 py-3 rounded font-bold text-white transition-all hover:scale-105"
              style={{ backgroundColor: "#10b981" }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen overflow-y-auto pb-32 pt-14"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* Fixed Header Bar - Always visible at top */}
      <div 
        className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between"
        style={{ 
          backgroundColor: "#0f172a",
          borderBottom: "2px solid rgba(16, 185, 129, 0.5)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)"
        }}
      >
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-all font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>← Exit</span>
        </button>
        
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 font-bold">CLIENT SCOPE OF WORK</span>
          <span className="text-slate-500">|</span>
          <span className="text-slate-400">{clientInfo?.project_name || "Project"}</span>
        </div>
        
        <button
          onClick={onClose}
          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Document Container */}
      <div 
        className="mx-auto mt-8"
        style={{ 
          maxWidth: "900px",
          backgroundColor: "#0f172a",
          borderRadius: "8px",
          border: "1px solid rgba(16, 185, 129, 0.3)",
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════════
            HEADER
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 pt-8 pb-6"
          style={{
            background: "linear-gradient(180deg, rgba(16, 185, 129, 0.15) 0%, transparent 100%)",
            borderBottom: "1px solid rgba(16, 185, 129, 0.3)",
            borderRadius: "8px 8px 0 0"
          }}
        >
          {/* Company Info */}
          <div className="mb-6">
            <h1 
              className="text-2xl font-bold tracking-wide"
              style={{ color: "#10b981", fontFamily: "'Playfair Display', serif" }}
            >
              {companySettings?.company_name || "Alpha Construction Pros"}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {companySettings?.address || "123 Builder Lane, Construction City, ST 12345"}
            </p>
            <p className="text-slate-500 text-xs mt-1">
              {companySettings?.email || "info@company.com"} • {companySettings?.phone || "(555) 123-4567"}
            </p>
          </div>
          
          {/* Document Metadata */}
          <div className="flex items-center gap-8 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">DOCUMENT ID</p>
              <input
                type="text"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                className="text-white font-bold text-sm bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">DATE</p>
              <p className="text-white font-bold text-sm">{formatDate()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">VALID FOR</p>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={validDays}
                  onChange={(e) => setValidDays(e.target.value)}
                  className="text-white font-bold text-sm bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none w-8 text-center"
                />
                <span className="text-white font-bold text-sm">Days</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">CONTRACT VALUE</p>
              <p className="text-emerald-400 font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {fmt(grandTotal)}
              </p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            TITLE & CLIENT INFO
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-5 w-5 text-emerald-400" />
            <p className="text-xs uppercase tracking-[4px] text-emerald-400">SCOPE OF WORK</p>
          </div>
          <h2 
            className="text-3xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif", lineHeight: 1.2 }}
          >
            {clientInfo?.project_name || estimateData?.job_name || "Project Scope of Work"}
          </h2>
          
          {/* Client Info Cards */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">PREPARED FOR</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-slate-500" />
                  <span className="text-white font-medium">{clientInfo?.name || "Client Name"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{clientInfo?.phone || "No phone on file"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{clientInfo?.email || "No email on file"}</span>
                </div>
              </div>
            </div>
            
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">PROJECT LOCATION</p>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-slate-500 mt-0.5" />
                <span className="text-white font-medium">{clientInfo?.address || "Address TBD"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            PROJECT OVERVIEW
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <Star className="h-4 w-4 text-emerald-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">PROJECT OVERVIEW</p>
          </div>
          <div 
            className="p-5 rounded-lg"
            style={{ backgroundColor: "rgba(30, 41, 59, 0.3)", borderLeft: "3px solid #10b981" }}
          >
            <textarea
              value={projectOverview}
              onChange={(e) => setProjectOverview(e.target.value)}
              className="w-full text-slate-300 bg-transparent border-none outline-none resize-none leading-relaxed"
              rows={4}
              placeholder="Enter project overview..."
            />
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SCOPE SECTIONS
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <ListChecks className="h-4 w-4 text-emerald-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">DETAILED SCOPE OF WORK</p>
          </div>
          
          <div className="space-y-4">
            {scopeSections.map((section, sectionIdx) => {
              const isExpanded = expandedSections[`scope-${sectionIdx}`] !== false;
              
              return (
                <div 
                  key={section.id}
                  className="rounded-lg overflow-hidden"
                  style={{ border: "1px solid rgba(16, 185, 129, 0.3)" }}
                >
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(`scope-${sectionIdx}`)}
                    className="w-full px-5 py-4 flex items-center justify-between"
                    style={{ backgroundColor: "rgba(16, 185, 129, 0.15)" }}
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                      <span className="text-white font-bold text-lg">{section.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-emerald-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {fmt(section.total)}
                      </span>
                      {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="p-5" style={{ backgroundColor: "rgba(15, 23, 42, 0.5)" }}>
                      {/* Scope Description - Editable */}
                      <textarea
                        value={section.description}
                        onChange={(e) => handleEditScopeDescription(sectionIdx, e.target.value)}
                        className="w-full text-slate-300 bg-transparent border border-slate-700 rounded p-3 outline-none focus:border-emerald-500 resize-none mb-4"
                        rows={3}
                      />
                      
                      {/* Items */}
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Included Items:</p>
                        {section.items.map((item, itemIdx) => (
                          <div key={item.id} className="flex items-center gap-3 text-slate-300">
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                            <span>{item.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            PROJECT TIMELINE
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">PROJECT TIMELINE</p>
            </div>
            <button
              onClick={handleAddTimeline}
              className="flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Phase
            </button>
          </div>
          
          <div 
            className="p-5 rounded-lg"
            style={{ backgroundColor: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.3)" }}
          >
            <div className="space-y-3">
              {projectTimeline.map((phase, idx) => (
                <div key={phase.id} className="flex items-center gap-4 group">
                  <span 
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ backgroundColor: "rgba(245, 158, 11, 0.2)", color: "#fbbf24" }}
                  >
                    {idx + 1}
                  </span>
                  <input
                    type="text"
                    value={phase.phase}
                    onChange={(e) => handleEditTimeline(idx, 'phase', e.target.value)}
                    className="flex-1 text-white bg-transparent border-b border-transparent hover:border-amber-500 focus:border-amber-500 outline-none font-medium"
                  />
                  <input
                    type="text"
                    value={phase.duration}
                    onChange={(e) => handleEditTimeline(idx, 'duration', e.target.value)}
                    className="w-24 text-amber-400 bg-transparent border-b border-transparent hover:border-amber-500 focus:border-amber-500 outline-none text-center"
                  />
                  <button
                    onClick={() => handleDeleteTimeline(idx)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            PAYMENT MILESTONES
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">PAYMENT MILESTONES</p>
            </div>
            <div className="flex items-center gap-4">
              <span className={`text-sm font-bold ${totalPercentage === 100 ? 'text-emerald-400' : 'text-red-400'}`}>
                Total: {totalPercentage}%
              </span>
              <button
                onClick={handleAddMilestone}
                className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm"
              >
                <Plus className="h-4 w-4" />
                Add Milestone
              </button>
            </div>
          </div>
          
          <div className="space-y-3">
            {paymentMilestones.map((milestone, idx) => (
              <div 
                key={milestone.id}
                className="p-4 rounded-lg group"
                style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.3)" }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={milestone.name}
                      onChange={(e) => handleEditMilestone(idx, 'name', e.target.value)}
                      className="text-white font-bold bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none w-full"
                    />
                    <input
                      type="text"
                      value={milestone.description}
                      onChange={(e) => handleEditMilestone(idx, 'description', e.target.value)}
                      className="text-slate-400 text-sm bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none w-full mt-1"
                    />
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    <div className="text-right">
                      <p className="text-emerald-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {fmt(grandTotal * (milestone.percentage / 100))}
                      </p>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={milestone.percentage}
                          onChange={(e) => handleEditMilestone(idx, 'percentage', e.target.value)}
                          className="w-12 text-slate-400 text-sm bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none text-right"
                        />
                        <span className="text-slate-400 text-sm">%</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteMilestone(idx)}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            TERMS & CONDITIONS
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-slate-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">TERMS & CONDITIONS</p>
            </div>
            <button
              onClick={handleAddTerm}
              className="flex items-center gap-1 text-slate-400 hover:text-slate-300 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Term
            </button>
          </div>
          
          <div 
            className="p-5 rounded-lg"
            style={{ backgroundColor: "rgba(30, 41, 59, 0.3)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
          >
            <ol className="space-y-2">
              {terms.map((term, idx) => (
                <li key={idx} className="flex items-start gap-3 group">
                  <span className="text-slate-500 flex-shrink-0">{idx + 1}.</span>
                  <input
                    type="text"
                    value={term}
                    onChange={(e) => handleEditTerm(idx, e.target.value)}
                    className="flex-1 text-slate-300 bg-transparent border-b border-transparent hover:border-slate-500 focus:border-slate-500 outline-none"
                  />
                  <button
                    onClick={() => handleDeleteTerm(idx)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            EXCLUSIONS
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 text-red-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">EXCLUSIONS (NOT INCLUDED)</p>
            </div>
            <button
              onClick={handleAddExclusion}
              className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Exclusion
            </button>
          </div>
          
          <div 
            className="p-5 rounded-lg"
            style={{ backgroundColor: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)" }}
          >
            <ul className="space-y-2">
              {exclusions.map((exclusion, idx) => (
                <li key={idx} className="flex items-start gap-3 group">
                  <X className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <input
                    type="text"
                    value={exclusion}
                    onChange={(e) => handleEditExclusion(idx, e.target.value)}
                    className="flex-1 text-slate-300 bg-transparent border-b border-transparent hover:border-red-500 focus:border-red-500 outline-none"
                  />
                  <button
                    onClick={() => handleDeleteExclusion(idx)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            AUTHORIZATION
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-10">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-4 w-4 text-slate-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">CLIENT AUTHORIZATION</p>
          </div>
          
          <div 
            className="p-6 rounded-lg"
            style={{ backgroundColor: "rgba(30, 41, 59, 0.3)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
          >
            <p className="text-slate-400 text-sm mb-6">
              By signing below, client acknowledges they have read and agree to the scope of work, payment terms, and conditions outlined in this document.
            </p>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Client Signature</p>
                <div 
                  className="h-16 rounded mb-2 flex items-center justify-center"
                  style={{ backgroundColor: "rgba(15, 23, 42, 0.5)", border: "1px dashed rgba(100, 116, 139, 0.3)" }}
                >
                  <span className="text-slate-600 text-sm">Sign Here</span>
                </div>
                <p className="text-slate-400 text-sm">Print Name: _______________________</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Date</p>
                <div 
                  className="h-16 rounded mb-2 flex items-center justify-center"
                  style={{ backgroundColor: "rgba(15, 23, 42, 0.5)", border: "1px dashed rgba(100, 116, 139, 0.3)" }}
                >
                  <span className="text-slate-400">{formatDate()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div 
          className="px-10 py-6 text-center"
          style={{
            borderTop: "1px solid rgba(100, 116, 139, 0.2)",
            backgroundColor: "rgba(15, 23, 42, 0.5)"
          }}
        >
          <p className="text-slate-500 text-xs">
            {companySettings?.company_name || "Alpha Construction Pros"} • Licensed & Insured
          </p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          FIXED BOTTOM ACTION BAR
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 px-6 py-4"
        style={{ 
          backgroundColor: "rgba(15, 23, 42, 0.95)",
          borderTop: "1px solid rgba(16, 185, 129, 0.3)",
          backdropFilter: "blur(10px)"
        }}
      >
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-emerald-400 hover:text-white hover:bg-slate-800 transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">← Back</span>
          </button>
          
          <button
            onClick={() => setSendModalOpen(true)}
            className="flex items-center gap-2 px-8 py-3 rounded-lg font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: "#10b981" }}
          >
            <Send className="h-5 w-5" />
            Send Scope of Work
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          SEND MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {sendModalOpen && (
        <div 
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
          onClick={() => setSendModalOpen(false)}
        >
          <div 
            className="w-full max-w-md p-6 rounded-lg"
            style={{ backgroundColor: "#1e293b", border: "1px solid rgba(16, 185, 129, 0.3)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-bold text-lg">Send Scope of Work</h3>
              <button onClick={() => setSendModalOpen(false)} className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              <button className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.3)" }}>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-emerald-400" />
                  <div>
                    <p className="text-white font-medium">Send via Email</p>
                    <p className="text-slate-500 text-sm">Email to client for review</p>
                  </div>
                </div>
              </button>
              
              <button className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.3)" }}>
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-5 w-5 text-blue-400" />
                  <div>
                    <p className="text-white font-medium">Send via SMS</p>
                    <p className="text-slate-500 text-sm">Text link to client</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={async () => {
                  if (!estimateId) {
                    toast.error("Please select an estimate first to generate a shareable link");
                    return;
                  }
                  if (!publicToken) {
                    toast.error("Please wait for document to be created");
                    return;
                  }
                  await saveSOW();
                  const link = `${window.location.origin}/sow-client/${publicToken}`;
                  try {
                    await navigator.clipboard.writeText(link);
                    setLinkCopied(true);
                    toast.success("Link copied!");
                    setTimeout(() => setLinkCopied(false), 3000);
                  } catch (error) {
                    toast.error("Failed to copy");
                  }
                }}
                disabled={!estimateId || loading}
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{ 
                  backgroundColor: linkCopied ? "rgba(16, 185, 129, 0.15)" : "rgba(30, 41, 59, 0.5)", 
                  border: linkCopied ? "1px solid rgba(16, 185, 129, 0.5)" : "1px solid rgba(100, 116, 139, 0.3)",
                  opacity: !estimateId || loading ? 0.6 : 1
                }}>
                <div className="flex items-center gap-3">
                  {linkCopied ? <Check className="h-5 w-5 text-emerald-400" /> : <Link2 className="h-5 w-5 text-amber-400" />}
                  <div>
                    <p className="text-white font-medium">
                      {!estimateId ? "Select Estimate First" : loading ? "Creating..." : !publicToken ? "Preparing..." : linkCopied ? "Link Copied!" : "Copy Link"}
                    </p>
                    <p className="text-slate-500 text-sm">
                      {!estimateId ? "An estimate must be selected" : "Share direct link"}
                    </p>
                  </div>
                </div>
              </button>
              
              <button className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.3)" }}>
                <div className="flex items-center gap-3">
                  <Download className="h-5 w-5 text-purple-400" />
                  <div>
                    <p className="text-white font-medium">Create PDF</p>
                    <p className="text-slate-500 text-sm">Download printable version</p>
                  </div>
                </div>
              </button>
            </div>
            
            <button onClick={() => setSendModalOpen(false)} className="w-full mt-6 py-3 rounded-lg text-slate-400 hover:text-white transition-colors"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSOWBuilder;
